import { useState, useEffect, useCallback } from 'react';
import { WorkItem } from '../api/workItems';

interface UseTriageNavigationProps {
    items: WorkItem[];
    onOpen: (item: WorkItem, activator?: HTMLElement | null) => void;
    onArchive?: (ids: string[]) => void;
    onDone?: (ids: string[]) => void;
    onSnooze?: (ids: string[]) => void;
    /** When false, window triage shortcuts (j/k/d/e/s, etc.) do not run — e.g. while the list drawer is open. Focus inside `role="dialog"` / `aria-modal` is ignored regardless. */
    shortcutsEnabled?: boolean;
}

export const useTriageNavigation = ({
    items,
    onOpen,
    onArchive,
    onDone,
    onSnooze,
    shortcutsEnabled = true,
}: UseTriageNavigationProps) => {
    const [focusedId, setFocusedId] = useState<string | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [lastClickedId, setLastClickedId] = useState<string | null>(null);
    const [focusSource, setFocusSource] = useState<'mouse' | 'keyboard' | null>(null);

    // Reset focus when items change substantially, but try to keep if it exists
    useEffect(() => {
        if (focusedId && !items.find(i => i.id === focusedId)) {
            setFocusedId(null);
        }

        if (selectedIds.size > 0) {
            const idsInList = new Set(items.map((i) => i.id));
            let didChange = false;
            const nextSelected = new Set<string>();
            selectedIds.forEach((id) => {
                if (idsInList.has(id)) {
                    nextSelected.add(id);
                } else {
                    didChange = true;
                }
            });
            if (didChange) {
                setSelectedIds(nextSelected);
            }
        }
    }, [items, focusedId, selectedIds]);

    const toggleSelection = useCallback((id: string, multiSelect?: boolean) => {
        setSelectedIds(prev => {
            const newSet = new Set(multiSelect ? prev : []);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
        setLastClickedId(id);
        setFocusedId(id);
        // source is set by the caller (keyboard handler or click handler)
    }, []);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (!shortcutsEnabled) {
            return;
        }
        const rawTarget = e.target;
        const target =
            rawTarget instanceof Element
                ? rawTarget
                : document.activeElement instanceof Element
                  ? document.activeElement
                  : null;
        if (!target) {
            return;
        }
        // Do not triage the list while a modal or drawer owns focus (MUI Dialog/Drawer, custom overlays).
        if (target.closest('[role="dialog"]') || target.closest('[aria-modal="true"]')) {
            return;
        }
        const ht = target as HTMLElement;
        // Ignore if input/textarea is active
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(ht.tagName) || ht.isContentEditable) {
            return;
        }

        const currentIndex = focusedId ? items.findIndex(i => i.id === focusedId) : -1;

        switch (e.key) {
            case 'j':
            case 'ArrowDown': {
                // Arrow keys scroll the page/column until a card is focused.
                // j always starts keyboard navigation (from the first card if needed).
                if (e.key.startsWith('Arrow') && currentIndex < 0) {
                    break;
                }
                e.preventDefault();
                const next = Math.min(items.length - 1, currentIndex + 1);
                setFocusSource('keyboard');
                setFocusedId(items[next]?.id || null);
                break;
            }
            case 'k':
            case 'ArrowUp': {
                if (e.key.startsWith('Arrow') && currentIndex < 0) {
                    break;
                }
                e.preventDefault();
                const prev = Math.max(0, currentIndex - 1);
                setFocusSource('keyboard');
                setFocusedId(items[prev]?.id || null);
                break;
            }
            case 'Enter': {
                if (focusedId) {
                    e.preventDefault();
                    const item = items.find(i => i.id === focusedId);
                    if (item) onOpen(item);
                }
                break;
            }
            case ' ': { // Space to toggle selection
                if (!focusedId) {
                    break;
                }
                e.preventDefault();
                toggleSelection(focusedId, true);
                break;
            }
            case 'Escape': {
                e.preventDefault();
                setSelectedIds(new Set());
                setFocusedId(null);
                break;
            }
            case 'd':
            case 'D': {
                if (onDone) {
                    const ids = selectedIds.size > 0 ? Array.from(selectedIds) : (focusedId ? [focusedId] : []);
                    if (ids.length) {
                        e.preventDefault();
                        onDone(ids);
                        // Clear selection after action
                        setSelectedIds(new Set());
                    }
                }
                break;
            }
            case 'e':
            case 'E': {
                if (onArchive) {
                    const ids = selectedIds.size > 0 ? Array.from(selectedIds) : (focusedId ? [focusedId] : []);
                    if (ids.length) {
                        e.preventDefault();
                        onArchive(ids);
                        setSelectedIds(new Set());
                    }
                }
                break;
            }
            case 's':
            case 'S': {
                if (onSnooze) {
                    const ids = selectedIds.size > 0 ? Array.from(selectedIds) : (focusedId ? [focusedId] : []);
                    if (ids.length) {
                        e.preventDefault();
                        onSnooze(ids);
                        setSelectedIds(new Set());
                    }
                }
                break;
            }
            case '/': {
                const byTestId = document.querySelector('[data-testid="WB_SEARCH"]') as HTMLInputElement | null;
                const searchInput =
                    byTestId ?? (document.querySelector('input[placeholder*="Search"]') as HTMLInputElement | null);
                if (searchInput) {
                    e.preventDefault();
                    searchInput.focus();
                }
                break;
            }
            // Add Select All (Ctrl/Cmd + A) logic if needed later
        }
    }, [items, focusedId, selectedIds, onOpen, onDone, onArchive, onSnooze, toggleSelection, shortcutsEnabled]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const handleItemClick = (id: string, e: React.MouseEvent) => {
        if (e.shiftKey && lastClickedId && items.some(i => i.id === lastClickedId)) {
            // Range selection
            const start = items.findIndex(i => i.id === lastClickedId);
            const end = items.findIndex(i => i.id === id);
            const min = Math.min(start, end);
            const max = Math.max(start, end);

            const rangeIds = items.slice(min, max + 1).map(i => i.id);
            setSelectedIds(prev => {
                const newSet = new Set(prev);
                rangeIds.forEach(rid => newSet.add(rid));
                return newSet;
            });
            setFocusSource('mouse');
            setFocusedId(id);
            setLastClickedId(id);
        } else if (e.metaKey || e.ctrlKey) {
            setFocusSource('mouse');
            toggleSelection(id, true);
        } else {
            setFocusSource('mouse');
            setFocusedId(id);
            setLastClickedId(id);
            // Plain click opens the item, matching Today and single-click conventions.
            // Shift / Ctrl / Cmd clicks and the checkbox still handle selection.
            const item = items.find(i => i.id === id);
            if (item) onOpen(item, e.currentTarget as HTMLElement | undefined);
        }
    };

    const handleItemDoubleClick = (id: string, e?: React.MouseEvent) => {
        const item = items.find(i => i.id === id);
        if (item) {
            setFocusSource('mouse');
            setFocusedId(id);
            setLastClickedId(id);
            onOpen(item, e?.currentTarget as HTMLElement | undefined);
        }
    };

    const handleCheckboxClick = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (e.shiftKey && lastClickedId) {
            // Range select via checkbox
            const start = items.findIndex(i => i.id === lastClickedId);
            const end = items.findIndex(i => i.id === id);
            const min = Math.min(start, end);
            const max = Math.max(start, end);

            const rangeIds = items.slice(min, max + 1).map(i => i.id);
            setSelectedIds(prev => {
                const next = new Set(prev);
                rangeIds.forEach(rid => next.add(rid));
                return next;
            });
        } else {
            setSelectedIds(prev => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
        }
        setFocusSource('mouse');
        setLastClickedId(id);
        setFocusedId(id);
    };

    return {
        focusedId,
        setFocusedId,
        focusSource,
        setFocusSource,
        selectedIds,
        setSelectedIds,
        handleItemClick,
        handleItemDoubleClick,
        handleCheckboxClick
    };
};
